import { useEffect } from 'react'
import { AnimatePresence, motion, useDragControls, type PanInfo } from 'framer-motion'
import { sheetBackdrop, sheetPanel } from './motion'

interface Props {
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  /** Hides the grab handle for sheets dismissed by a button instead. */
  showHandle?: boolean
  /** Caps the panel height. Content inside scrolls. */
  maxHeight?: string
  className?: string
  'aria-label'?: string
  'data-testid'?: string
  /** Rendered inside the handle row, on the right — usually a close button. */
  handleAction?: React.ReactNode
}

/**
 * How far, or how fast, counts as "put it away".
 *
 * Its own function because it is the only part of the gesture we actually own —
 * the pointer plumbing belongs to framer-motion. The velocity clause is what
 * makes a quick flick work when your thumb barely travelled, which is how a
 * sheet gets closed one-handed while you are standing under a wall.
 */
export function shouldDismissSheet(offsetY: number, velocityY: number): boolean {
  return offsetY > 110 || velocityY > 500
}

/**
 * The app's one bottom sheet.
 *
 * There used to be two hand-rolled versions: `RouteDetailSheet` had a good one
 * driven by manual transform state, and `FilterSheet` had the same markup with
 * the animation left out, so it simply appeared.
 *
 * The drag starts from the handle and nowhere else. That is not a detail — the
 * route sheet is tall, scrolls, and has a photo you swipe sideways to reach the
 * next route. A panel-wide drag would fight all three.
 */
export function Sheet({
  isOpen,
  onClose,
  children,
  showHandle = true,
  maxHeight = '90vh',
  className = '',
  'aria-label': ariaLabel,
  'data-testid': testId,
  handleAction,
}: Props) {
  const dragControls = useDragControls()

  // A sheet over a page that still scrolls behind it feels broken, and on iOS
  // the page only keeps its scroll position if we lock rather than reposition.
  useEffect(() => {
    if (!isOpen) return
    const previousOverflow = document.body.style.overflow
    const previousOverscroll = document.body.style.overscrollBehaviorY
    document.body.style.overflow = 'hidden'
    document.body.style.overscrollBehaviorY = 'none'
    return () => {
      document.body.style.overflow = previousOverflow
      document.body.style.overscrollBehaviorY = previousOverscroll
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  const handleDragEnd = (_: unknown, info: PanInfo) => {
    if (shouldDismissSheet(info.offset.y, info.velocity.y)) onClose()
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[var(--z-sheet)] flex items-end justify-center">
          <motion.div
            data-testid="sheet-backdrop"
            variants={sheetBackdrop}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClose}
            className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel}
            data-testid={testId}
            variants={sheetPanel}
            initial="hidden"
            animate="visible"
            exit="exit"
            drag="y"
            dragListener={false}
            dragControls={dragControls}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.6 }}
            onDragEnd={handleDragEnd}
            style={{ maxHeight }}
            className={`relative flex w-full max-w-xl flex-col overflow-hidden rounded-t-xl bg-content1 pb-[var(--safe-b)] shadow-e3 ${className}`}
          >
            {showHandle && (
              <div
                data-testid="drag-handle"
                onPointerDown={(e) => dragControls.start(e)}
                className="flex shrink-0 touch-none items-center px-3 pb-1 pt-3"
              >
                <span className="flex-1" />
                <span
                  aria-hidden
                  className="h-1 w-9 cursor-grab rounded-full bg-foreground/15 active:cursor-grabbing"
                />
                <span className="flex flex-1 justify-end">{handleAction}</span>
              </div>
            )}
            {children}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}
